/**
 * Base class for all entity types in the system.
 * Provides common properties and functionality for entity objects.
 * @class EntityBase
 */
export class EntityBase {
  /**
   * Unique identifier for the entity (GUID).
   * @type {string}
   * @default ""
   * @example "123e4567-e89b-12d3-a456-426614174000"
   */
  public id: string = "";

  /**
   * Human-readable name of the entity.
   * @type {string}
   * @default ""
   * @example "My Entity Name"
   */
  public name: string = "";

  /**
   * Soft-delete status of this entity.
   * When false, the entity is considered deleted but remains in the database.
   * @type {boolean}
   * @default true
   */
  public isActive: boolean = true;

  /**
   * Identifier of the user who created this entity.
   * Can be a user ID, username, or email depending on the system implementation.
   * @type {string}
   * @default "system"
   * @example "user123", "john.doe@example.com", or "550e8400-e29b-41d4-a716-446655440000"
   */
  public createdBy: string = "system";

  /**
   * Timestamp when this entity was created.
   * @type {Date | undefined}
   * @default undefined
   */
  public createdOn?: Date;

  /**
   * Timestamp when this entity was last updated.
   * This field is automatically updated whenever the entity is modified.
   * @type {Date | undefined}
   * @default undefined
   */
  public updatedOn?: Date;

  /**
   * Identifier of the user who last updated this entity.
   * Can be a user ID, username, or email depending on the system implementation.
   * @type {string}
   * @default "system"
   * @example "user456", "jane.smith@example.com", or "6ba7b810-9dad-11d1-80b4-00c04fd430c9"
   */
  public updatedBy: string = "system";

  /**
   * Gets the runtime type of the entity object.
   * Useful for type checking and serialization scenarios.
   * @returns {string} The constructor name of the entity
   * @readonly
   * @example "CourseEntity", "CustomerEntity", "LabEntity", etc.
   */
  public get entityType(): string {
    return this.constructor.name;
  }
}
